import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { RootState } from '../../redux/store';
import { useAppSelector } from '../../redux/store/hooks';
import { useCreateMockPlanMutation } from '../../services/payments';
import { useFetchMockTestPricingQuery } from '../../services/startTestAPI/startTestApi';
import Button from '../Common/Button';
import CommonDialog from '../Common/Dialog';
import TestResults from '../Features/TestResults';

// ── Types ─────────────────────────────────────────────────────────────────────
type PricingTier = {
  id: string;
  isActive: boolean;
  minScore: number;
  maxScore: number;
  price: number;
  originalPrice: number;
  durationInMonths: number;
};

// ── Score ring ────────────────────────────────────────────────────────────────
const ScoreRing = ({ percentage }: { percentage: number }) => {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;
  const color =
    percentage >= 70 ? '#16a34a' : percentage >= 40 ? '#d97706' : '#dc2626';

  return (
    <svg width='100' height='100' viewBox='0 0 100 100' className='mx-auto'>
      <circle
        cx='50'
        cy='50'
        r={radius}
        fill='none'
        stroke='#e5e7eb'
        strokeWidth='10'
      />
      <circle
        cx='50'
        cy='50'
        r={radius}
        fill='none'
        stroke={color}
        strokeWidth='10'
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap='round'
        transform='rotate(-90 50 50)'
        style={{ transition: 'stroke-dashoffset 0.8s ease' }}
      />
      <text
        x='50'
        y='55'
        textAnchor='middle'
        fontSize='18'
        fontWeight='700'
        fill={color}
      >
        {percentage}%
      </text>
    </svg>
  );
};

// ── Discount banner ───────────────────────────────────────────────────────────
const DiscountBanner = ({
  tier,
  percentage
}: {
  tier: PricingTier | null;
  percentage: number;
}) => {
  const [createMockPlan] = useCreateMockPlanMutation();
  const { id } = useParams();

  if (!tier) return null;
  const discountAmount = tier.originalPrice - tier.price;
  const discountPercent = Math.round(
    (discountAmount / tier.originalPrice) * 100
  );
  const isFree = tier.price === 0;

  const genratePaymentLink = async () => {
    try {
      const res = await createMockPlan({
        userId: id,
        planId: tier.id,
        // selectedModules: [],
        // couponApplied: false,
        finalAmount: tier.price
        // couponCodeId: ''
      }).unwrap();
      if (res.status === 'success') {
        const myWindow = window.open('', '_self');
        if (myWindow) {
          myWindow.document.open();
          myWindow.document.write(res.data.paymentUrl);
          myWindow.document.close();
        }
      }
    } catch (err) {
      console.log(err, 'err');
    }
  };
  if (isFree) {
    return (
      <div className='w-full rounded-xl border border-green-300 bg-green-50 px-4 py-3 text-center shadow-sm'>
        <p className='text-sm font-semibold text-green-800'>
          🎉 Congratulations! You scored {percentage}%
        </p>
        <p className='mb-3 mt-1 text-sm text-green-700'>
          You&apos;ve earned <span className='font-bold'>FREE access</span> to
          our full course (worth ₹{tier.originalPrice}) for{' '}
          {tier.durationInMonths} months!
        </p>
        <Button
          label='Claim Your Free Access'
          variant='contained'
          handleClickBtn={() => {
            genratePaymentLink();
          }}
        />
      </div>
    );
  }

  if (discountAmount > 0) {
    return (
      <div className='w-full rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-center shadow-sm'>
        <p className='text-sm font-semibold text-amber-800'>
          🏅 You scored {percentage}% — you&apos;ve unlocked a discount!
        </p>
        <p className='mb-3 mt-1 text-sm text-amber-700'>
          Get our full course for{' '}
          <span className='font-bold text-amber-900'>₹{tier.price}</span>{' '}
          <span className='text-amber-500 line-through'>
            ₹{tier.originalPrice}
          </span>{' '}
          — save ₹{discountAmount} ({discountPercent}% off) for{' '}
          {tier.durationInMonths} months.
        </p>
        <Button
          label='Claim Your Discount'
          variant='contained'
          handleClickBtn={() => {
            genratePaymentLink();
          }}
        />
      </div>
    );
  }

  // No discount (full price)
  return (
    <div className='w-full rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-center shadow-sm'>
      <p className='text-sm font-semibold text-blue-800'>📘 Keep practising!</p>
      <p className='mb-3 mt-1 text-sm text-blue-700'>
        You scored {percentage}%. Improve your performance and master the
        concepts with our full course (₹{tier.originalPrice}).
      </p>
      <Button
        label='Explore Our Course'
        variant='contained'
        handleClickBtn={() => {
          genratePaymentLink();
        }}
      />
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const MockResults = () => {
  const { data, error, isLoading } = useFetchMockTestPricingQuery({});
  const [isDialogOpen, setIsDialogOpen] = useState(true);

  const { userTestId, resultById } = useAppSelector(
    (state: RootState) => state.root.startFulltest
  );

  // ── Score calculations ──────────────────────────────────────────────────────
  const totalGiven: number = resultById?.totalQuestionsGiven ?? 0;
  const totalCorrect: number = resultById?.totalQuestionsCorrect ?? 0;
  const totalAttempted: number = resultById?.totalQuestionsAttempted ?? 0;
  const totalWrong: number = totalAttempted - totalCorrect;
  const percentage: number =
    totalGiven > 0 ? Math.round((totalCorrect / totalGiven) * 100) : 0;

  // ── Match percentage to pricing tier ───────────────────────────────────────
  // Find the active tier where the student's score falls between minScore & maxScore
  const pricingTiers: PricingTier[] = data?.data ?? [];
  const matchedTier: PricingTier | null =
    pricingTiers.find(
      tier =>
        tier.isActive &&
        percentage >= tier.minScore &&
        percentage <= tier.maxScore
    ) ?? null;

  useEffect(() => {
    if (error) {
      console.error('Error fetching mock test pricing:', error, resultById);
    }
  }, [data, error, isLoading, resultById]);

  return (
    <>
      <CommonDialog isOpen={isDialogOpen} close={() => setIsDialogOpen(false)}>
        <div className='flex flex-col items-center gap-5 p-4'>
          {/* Score ring */}
          <ScoreRing percentage={percentage} />

          {/* Stats row */}
          <div className='flex w-full justify-around text-center'>
            <div>
              <p className='text-2xl font-bold text-gray-800'>{totalGiven}</p>
              <p className='text-xs text-gray-500'>Total</p>
            </div>
            <div>
              <p className='text-2xl font-bold text-blue-600'>
                {totalAttempted}
              </p>
              <p className='text-xs text-gray-500'>Attempted</p>
            </div>
            <div>
              <p className='text-2xl font-bold text-green-600'>
                {totalCorrect}
              </p>
              <p className='text-xs text-gray-500'>Correct</p>
            </div>
            <div>
              <p className='text-2xl font-bold text-red-500'>{totalWrong}</p>
              <p className='text-xs text-gray-500'>Wrong</p>
            </div>
          </div>

          {/* Discount banner — driven by matched pricing tier */}
          <DiscountBanner tier={matchedTier} percentage={percentage} />

          {/* Email notice */}
          <div className='w-full rounded-lg border border-gray-200 bg-gray-50 p-3 text-center text-sm text-gray-600 shadow-sm'>
            <p className='font-medium'>Results Sent to Your Email</p>
            <p className='mt-1'>
              We have sent your test results and offer details to your
              registered email address.
            </p>
          </div>
        </div>
      </CommonDialog>

      <TestResults />
    </>
  );
};

export default MockResults;
